<template>
  <div class="container">
    <p>{{ t('capsuleCannabinoidContentCalculation') }}</p>
    <EquationBlock :equations="equations" />
    <hr />
    <div class="row">
      <div class="col-sm">
        <GenericInput
          :input-label="t('concentrateVolume')"
          v-model.number="concentrateVol"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <GenericInput
          :input-label="t('fillerVolume')"
          v-model.number="fillerVol"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <GenericInput
          :input-label="t('containerVolume')"
          v-model.number="containerVol"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <h5>{{ t('extractTestResults') }}</h5>
        <FixedUnitInput
          :input-label="t('THC')"
          v-model.number="testTHC"
          :unit="t('percent')"
        />
        <FixedUnitInput
          :input-label="t('THCa')"
          v-model.number="testTHCa"
          :unit="t('percent')"
        />
        <FixedUnitInput
          :input-label="t('delta8THC')"
          v-model.number="testDelta8THC"
          :unit="t('percent')"
        />
        <FixedUnitInput
          :input-label="t('CBD')"
          v-model.number="testCBD"
          :unit="t('percent')"
        />
        <FixedUnitInput
          :input-label="t('CBN')"
          v-model.number="testCBN"
          :unit="t('percent')"
        />
        <FixedUnitInput
          :input-label="t('CBG')"
          v-model.number="testCBG"
          :unit="t('percent')"
        />
      </div>
      <div class="col-sm">
        <GenericOutput
          :output-label="t('capCartTHC')"
          :number="cannabinoids[0]"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <GenericOutput
          :output-label="t('capCartTHCa')"
          :number="cannabinoids[1]"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <GenericOutput
          :output-label="t('capCartDelta8THC')"
          :number="cannabinoids[2]"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <GenericOutput
          :output-label="t('capCartCBD')"
          :number="cannabinoids[3]"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <GenericOutput
          :output-label="t('capCartCBN')"
          :number="cannabinoids[4]"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <GenericOutput
          :output-label="t('capCartCBG')"
          :number="cannabinoids[5]"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <GenericOutput
          :output-label="t('capCartTotalCannabinoids')"
          :number="cannabinoids.reduce((acc, val) => acc + val, 0)"
          :conversionFactors="ConversionFactors.tinyVolume"
        />
        <FixedUnitOutput
          :output-label="t('capsCartsMade')"
          :number="capsCartsMade"
          :unit="t('units')"
        />
      </div>
      <p>{{ t('CapsuleCalcApproximateConversionNote') }}</p>
    </div>
  </div>
</template>


<script>
import { ref, computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import GenericInput from '../inputs/GenericInput.vue'; // Adjust import path as needed
import GenericOutput from '../inputs/GenericOutput.vue'; // Adjust import path as needed
import FixedUnitInput from '../inputs/FixedUnitInput.vue';
import FixedUnitOutput from '../inputs/FixedUnitOutput.vue'; // Adjust import path as needed
import EquationBlock from '../inputs/EquationBlock.vue'; // Adjust import path as needed
import ConversionFactors from '../../utils/conversion_factors';
import { defaultRound } from '../../utils/math';

export default {
  components: {
    GenericInput,
    GenericOutput,
    FixedUnitInput,
    FixedUnitOutput,
    EquationBlock
  },
  setup() {
    const concentrateVol = ref(5.7);
    const fillerVol = ref(150);
    const containerVol = ref(30);
    const testTHC = ref(50);
    const testTHCa = ref(0.5);
    const testDelta8THC = ref(0.03);
    const testCBD = ref(2.5);
    const testCBN = ref(5);
    const testCBG = ref(0.11);

    const { t, tm } = useI18n();

    // Create a reactive object with a function
    const translations = reactive({
      t: (key) => t(key)
    })

    const equations = computed(() => {
        return tm('capsuleCalcEquations');
    });

    const cannabinoids = computed(() => {
        const mlTHCInExtract = testTHC.value / 100 * concentrateVol.value;
        const mlTHCaInExtract = testTHCa.value / 100 * concentrateVol.value;
          const mlDelta8THCInExtract = testDelta8THC.value / 100 * concentrateVol.value;
          const mlCBDInExtract = testCBD.value / 100 * concentrateVol.value;
          const mlCBNInExtract = testCBN.value / 100 * concentrateVol.value;
          const mlCBGInExtract = testCBG.value / 100 * concentrateVol.value;

        // Convert THCa to THC
      // Repeat for other cannabinoids
      const capsulePercentOfTotalMix =
        containerVol.value / (fillerVol.value + concentrateVol.value);
      return [
        mlTHCInExtract * capsulePercentOfTotalMix,
        mlTHCaInExtract * capsulePercentOfTotalMix,
        mlDelta8THCInExtract * capsulePercentOfTotalMix,
        mlCBDInExtract * capsulePercentOfTotalMix,
        mlCBNInExtract * capsulePercentOfTotalMix,
        mlCBGInExtract * capsulePercentOfTotalMix
        // Repeat for other cannabinoids
      ];
    });

    const capsCartsMade = computed(() => {
      return defaultRound(
        (concentrateVol.value + fillerVol.value) / containerVol.value
      );
    });

    return {
      concentrateVol,
      fillerVol,
      containerVol,
      testTHC,
      testTHCa,
      testDelta8THC,
      testCBD,
      testCBN,
      testCBG,
      cannabinoids,
      capsCartsMade,
      equations,
      ConversionFactors,
      t: translations.t
    };
  }
};
</script>
